import { useMemo } from 'react';
import clsx from 'clsx';
import { cva } from 'class-variance-authority';
import { Flex, Text, Icon, iconWeight, Button } from '@nula/core-ui';
import { useTimeAwareImage, type TimeOfDay } from '@/hooks/use-time-aware-image';
import { useNavigateToEntity, useNavigateToWorkspaceView } from '@/hooks/navigation';
import { useWorkspaceDocs } from '@/lib/sync/hooks/use-workspace-docs';
import { useAuthStore } from '@/stores/use-auth-store';
import { MergedCalendarCard } from '@/features/calendar/components/merged-calendar-card';
import { useCreateEntity } from '@/lib/workspace/hooks/use-create-entity';
import { QuickActions } from './quick-actions';
import styles from './home.module.css';
import { HomeRecentCard } from './home-recent-card';

const root = cva(styles.root);
const contentWrapper = cva(styles.contentWrapper);
const greetingSection = cva(styles.greetingSection);
const quickActionsSection = cva(styles.quickActionsSection);
const calendarSection = cva(styles.calendarSection);
const docsGrid = cva(styles.docsGrid);
const sectionHeader = cva(styles.sectionHeader);
const viewAllLink = cva(styles.viewAllLink);

// ── Kind label/icon color by document type ─────────────────────────────────────
const DOC_TYPE_COLOR: Record<string, string> = {
  document:   'var(--pastel-mist-blue)',  // Cool blue — written page
  canvas:     'var(--pastel-sand)',       // Warm beige — structured canvas
  collection: 'var(--pastel-lavender)',   // Soft purple — folder/group
};

// Entity-kind labels & icons live in @utils/entityKindMeta — single
// source of truth for HomePage cards, calendar linked notes, search results,
// etc. Add new kinds there.

// ── HomePage ───────────────────────────────────────────────────────────────────

interface HomePageProps {
  timeOverride?: TimeOfDay;
}

export function HomePage({ timeOverride }: HomePageProps) {
  const { greeting } = useTimeAwareImage(timeOverride);
  const today = new Date();

  const userName = useAuthStore((s) => s.user?.name);
  const firstName = userName?.split(' ')[0] || '';

  const navigateToEntity = useNavigateToEntity();
  const navigateToView = useNavigateToWorkspaceView();
  const { createNewDocument, createNewCanvas } = useCreateEntity();

  const allDocs = useWorkspaceDocs();
  const allDocuments = useMemo(() => allDocs.filter((d) => !d.isArchived), [allDocs]);

  const recentDocs = useMemo(() => {
    return [...allDocuments]
      .filter((d) => !d.isArchived)
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      .slice(0, 3);
  }, [allDocuments]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return new Intl.DateTimeFormat(navigator.language, { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
  };

  return (
    <Flex
      vertical
      className={root()}
    >
      <Flex
        vertical
        className={contentWrapper()}
      >

        {/* ── Greeting ── */}
        <Flex vertical className={greetingSection()}>
          <Text className={styles.greetingTitle}>
            {greeting}{firstName ? `, ${firstName}` : ''}.
          </Text>
          <Text size="xs" className={styles.greetingDate}>
            {new Intl.DateTimeFormat(navigator.language, { weekday: 'long', month: 'long', day: 'numeric' }).format(today)}
          </Text>
        </Flex>

        {/* ── Quick actions ── */}
        <div className={quickActionsSection()}>
          <QuickActions />
        </div>

        {/* ── Calendar: day events + month picker merged into one full-width card ── */}
        <div className={calendarSection()}>
          <MergedCalendarCard onOpenCalendar={() => navigateToView('calendar')} />
        </div>

        {/* ── Recents: cards (or empty state) ── */}
        {recentDocs.length > 0 && (
          <Flex align="center" justify="end" className={sectionHeader()}>
            <Flex
              align="center"
              gap="xs"
              className={viewAllLink()}
              onClick={() => { navigateToView('documents'); }}
            >
              <Text size="xs" className={styles.viewAllText}>View all</Text>
              <Icon name="arrow_forward" size={12} weight={iconWeight.light} className={styles.viewAllIcon} />
            </Flex>
          </Flex>
        )}
        {recentDocs.length === 0 ? (
          <Flex vertical align="center" justify="center" gap="sm" className={styles.recentsEmpty}>
            <span className={styles.recentsEmptyIcon}>
              <Icon name="auto_awesome" size={22} weight={iconWeight.light} />
            </span>
            <Text className={styles.recentsEmptyTitle}>Nothing here yet</Text>
            <Text type="secondary" className={styles.recentsEmptyDesc}>
              Your recent notes and canvases will appear here as you work.
            </Text>
            <Flex gap="sm" className={styles.recentsEmptyActions}>
              <Button size="sm" variant="default" icon={<Icon name="description" size={14} weight={iconWeight.light} />} onClick={() => { void createNewDocument(); }}>
                New note
              </Button>
              <Button size="sm" variant="default" icon={<Icon name="gesture" size={14} weight={iconWeight.light} />} onClick={() => { void createNewCanvas(); }}>
                New canvas
              </Button>
            </Flex>
          </Flex>
        ) : (
          <div className={clsx('nula-grid-cols-3', docsGrid())}>
            {recentDocs.map((doc) => (
              <HomeRecentCard
                key={doc.id}
                id={doc.id}
                title={doc.title || 'Untitled'}
                updatedAt={formatDate(doc.updatedAt)}
                kind={doc.kind}
                color={DOC_TYPE_COLOR[doc.kind] || DOC_TYPE_COLOR.document}
                onClick={() => { navigateToEntity(doc.id); }}
              />
            ))}
          </div>
        )}

      </Flex>
    </Flex>
  );
}
